import _ from 'lodash';
import { createReducer } from 'redux-act';
import * as action from '../actions/infobox.actions';
import * as requestsActions from '../actions/requests.actions';
import { parseLoadedPayers, loadCoverages } from '../../parsers/payer.parser';
import parsePhone from '../../parsers/phone.parser';
import { payer } from '../../schemas/infobox.schema';
import coverageStateSchema, { createCoverage } from '../../schemas/coverageState.schema';
import { updateDynamicState } from './helpers/dynamicReducer.helper';

export const initialState = {
  coverages: {},
  coordinationTypes: [],
  patientFirstName: '',
  patientMiddleName: '',
  patientLastName: '',
  patientEmail: '',
  patientBirthday: null,
  patientSsn: '',
  patientMRN: '',
  patientWeight: '',
  patientHeight: '',
  patientHeightInches: '',
  comments: {},
  formValid: false,
  requesterFirstName: '',
  requesterLastName: '',
  requesterPhone: '',
  requesterExtension: '',
  notes: '',
  focusRequesterFirstName: false,
  eligibilityHTML: '',
  eligibilityLoading: false,
  isSubmitPatientInfoInProgress: false,
  requesterDirty: false,
  coveragesDirty: false,
  dirtyCoverages: {},
  notesDirty: false,
  verificationTypes: null,
  isBilledToFacility: false,
  hideSupplemental: false,
  tempBillToFacilityCoverage: null,
  billedToFacilityChange: false,
};

const updateCoverages = updateDynamicState('coverages', () => coverageStateSchema.parse({}));
const updateCoveragesWithoutRequired = (state, coordinationTypeId, updateObj) => {
  const newUpdateObj = { ...updateObj };
  delete newUpdateObj.isRequired;

  return updateCoverages(state, coordinationTypeId, newUpdateObj);
};

const withEmptyCoverageSlots = (coveragesWithPayer, coordinationTypes) =>
  coordinationTypes
    .map(ct => ct.id)
    .reduce((acc, id) => ({ ...acc, [id]: acc[id] || createCoverage({ coordinationTypeId: id }) }), {
      ...coveragesWithPayer,
    });

const mergeCoverageRefreshWithSlotState = (coverage, currentCoverage = {}) => ({
  ...coverage,
  isRequired: currentCoverage.isRequired ?? coverage.isRequired,
  payers: currentCoverage.payers?.length ? currentCoverage.payers : coverage.payers,
});

const getEmptyCoverageRefreshSlot = (coordinationTypeId, currentCoverage = {}) =>
  createCoverage({
    coordinationTypeId: Number(coordinationTypeId),
    isRequired: currentCoverage.isRequired || false,
  });

const setPayerWarnings = (coverages, payers) => {
  const coveragesWithWarnings = JSON.parse(JSON.stringify(coverages));

  Object.keys(coveragesWithWarnings).forEach(key => (coveragesWithWarnings[key].warnings = []));

  payers.forEach(p =>
    Object.keys(coverages).forEach(c => {
      const { payerId } = coverages[c].payer;
      if (payerId === p.payerId) {
        coveragesWithWarnings[c].warnings = p.warnings;
      }
    }),
  );

  return coveragesWithWarnings;
};

const last = state => state.slice(-1)[0];
const loadPatient = (patient, item) => (patient ? last(patient.demographics)[item] : '');
const loadRequester = (requester, item) => (requester ? last(requester.data)[item] : '');
const loadPatientHeight = payload => {
  const height = loadPatient(payload.patient, 'height');
  return height && height.split("'")[0];
};
const loadPatientHeightInches = payload => {
  const height = loadPatient(payload.patient, 'height');
  return height && height.split("'")[1].slice(0, -1);
};
const loadPatientMRN = payload => {
  const patientExternalIds = _.get(payload.patient, 'patientExternalId', []);
  const requesterAgencyGroupId = _.get(payload.requesterAgency, 'requesterAgencyGroup.id', null);

  const patientExternalId = patientExternalIds.find(
    externalId => externalId.requesterAgencyGroupId === requesterAgencyGroupId,
  );
  return patientExternalId && patientExternalId.externalId;
};

const infoboxReducer = createReducer(
  {
    [action.initCoverage]: (state, { coordinationTypeId, isRequired }) => ({
      ...state,
      ...updateCoverages(state, coordinationTypeId, {
        coordinationTypeId,
        isRequired,
      }),
    }),
    [action.setPayerInfo]: (state, { coordinationTypeId, ...payload }) => ({
      ...state,
      ...(coordinationTypeId === undefined || coordinationTypeId === null
        ? {}
        : {
            dirtyCoverages: {
              ...state.dirtyCoverages,
              [coordinationTypeId]: true,
            },
          }),
      ...updateCoveragesWithoutRequired(state, coordinationTypeId, payload),
    }),
    [action.replaceCoverages]: (state, coverages) => {
      const newCoverages = withEmptyCoverageSlots(coverages, state.coordinationTypes);

      return {
        ...state,
        coverages: newCoverages,
      };
    },
    [action.applyCoveragePartialRefresh]: (state, coverages) => {
      const refreshedCoverages = Object.keys(coverages || {}).reduce((acc, coordinationTypeId) => {
        const currentCoverage = state.coverages[coordinationTypeId];
        const refreshedCoverage = coverages[coordinationTypeId];

        return {
          ...acc,
          [coordinationTypeId]: refreshedCoverage
            ? mergeCoverageRefreshWithSlotState(refreshedCoverage, currentCoverage)
            : getEmptyCoverageRefreshSlot(coordinationTypeId, currentCoverage),
        };
      }, {});

      return {
        ...state,
        coverages: withEmptyCoverageSlots(
          {
            ...state.coverages,
            ...refreshedCoverages,
          },
          state.coordinationTypes,
        ),
      };
    },
    [action.clearPayer]: (state, coordinationTypeId) => ({
      ...state,
      ...(coordinationTypeId === undefined || coordinationTypeId === null
        ? {}
        : {
            dirtyCoverages: {
              ...state.dirtyCoverages,
              [coordinationTypeId]: true,
            },
          }),
      ...updateCoveragesWithoutRequired(state, coordinationTypeId, {
        payer: payer.parse({}),
        payerName: '',
        payerQuery: '',
        payerInput: '',
        policyNumber: '',
        authorizationNumber: '',
      }),
    }),
    [action.queryPayers]: (state, { coordinationTypeId, query }) => ({
      ...state,
      ...updateCoveragesWithoutRequired(state, coordinationTypeId, {
        payerInput: query,
      }),
    }),
    [action.setRequesterInfo]: (state, payload) => ({
      ...state,
      ...payload,
    }),
    [action.setPatientDetails]: (state, payload) => ({
      ...state,
      ...payload,
    }),
    [requestsActions.applyPatientPartialRefresh]: (state, payload) => ({
      ...state,
      ...payload.infoboxFields,
    }),
    [action.setRequesterPhone]: (state, payload) => ({
      ...state,
      requesterPhone: parsePhone(payload),
    }),
    [action.setNotes]: (state, payload) => ({
      ...state,
      ...payload,
    }),
    [action.setRequester]: (state, payload) => ({
      ...state,
      requesterFirstName: payload.firstName,
      requesterLastName: payload.lastName,
      requesterPhone: payload.phone,
      requesterExtension: payload.phoneExtension,
    }),
    [action.fetchPayersSuccess]: (state, { payers, coordinationTypeId }) => ({
      ...state,
      ...updateCoveragesWithoutRequired(state, coordinationTypeId, {
        payers: parseLoadedPayers(payers),
      }),
    }),
    [action.setBirthday]: (state, payload) => ({
      ...state,
      patientBirthday: payload,
    }),
    [action.setValid]: (state, payload) => ({
      ...state,
      formValid: payload,
    }),
    [action.loadRequestDetails]: (state, payload) => {
      return {
        ...state,
        requesterFirstName: loadRequester(payload.requester, 'firstName'),
        requesterLastName: loadRequester(payload.requester, 'lastName'),
        requesterPhone: loadRequester(payload.requester, 'phone'),
        requesterExtension: loadRequester(payload.requester, 'extension'),
        patientFirstName: loadPatient(payload.patient, 'firstName'),
        patientMiddleName: loadPatient(payload.patient, 'middleName'),
        patientLastName: loadPatient(payload.patient, 'lastName'),
        patientEmail: loadPatient(payload.patient, 'email'),
        patientBirthday: loadPatient(payload.patient, 'dob') || null,
        patientSsn: loadPatient(payload.patient, 'ssn'),
        patientMRN: loadPatientMRN(payload),
        patientHeight: loadPatientHeight(payload),
        patientHeightInches: loadPatientHeightInches(payload),
        patientWeight: loadPatient(payload.patient, 'weight'),
      };
    },
    [action.loadCoverages]: (state, payload) => ({
      ...state,
      coverages: loadCoverages(payload),
      coveragesDirty: false,
      dirtyCoverages: {},
      loadCoverageInProgress: false,
    }),
    [action.setLoadCoverageInProgress]: (state, payload) => ({
      ...state,
      loadCoverageInProgress: payload,
    }),
    [action.loadComments]: (state, payload) => ({
      ...state,
      notes: payload,
    }),
    [action.setFocusRequesterFirstName]: (state, payload) => ({
      ...state,
      focusRequesterFirstName: payload,
    }),
    [action.setSubmitPatientInfoInProgress]: (state, payload) => ({
      ...state,
      isSubmitPatientInfoInProgress: payload,
    }),
    [action.setRequesterDirty]: (state, payload) => ({
      ...state,
      requesterDirty: payload,
    }),
    [action.setCoveragesDirty]: (state, payload) => ({
      ...state,
      coveragesDirty: payload,
      dirtyCoverages: payload ? state.dirtyCoverages : {},
    }),
    [action.setNotesDirty]: (state, payload) => ({
      ...state,
      notesDirty: payload,
    }),
    [action.checkPayerRulesResponse]: (state, payload) => ({
      ...state,
      coverages: setPayerWarnings(state.coverages, payload),
    }),
    [action.resetInfobox]: () => ({
      ...initialState,
    }),
    [action.setVerificationTypes]: (state, payload) => ({
      ...state,
      verificationTypes: payload,
    }),
    [action.setBilledToFacility]: (state, payload) => ({
      ...state,
      isBilledToFacility: payload,
      hideSupplemental: payload,
    }),
    [action.applyBilledToFacilityPartialRefresh]: (state, payload) => ({
      ...state,
      isBilledToFacility: payload,
      hideSupplemental: payload,
      billedToFacilityChange: false,
    }),
    [action.setBillToFacilityCoverage]: (state, payload) => ({
      ...state,
      tempBillToFacilityCoverage: payload,
    }),
    [action.billedToFacilityChange]: (state, payload) => ({
      ...state,
      billedToFacilityChange: payload,
    }),
    [action.setCoordinationTypes]: (state, payload) => ({
      ...state,
      coordinationTypes: payload,
    }),
  },
  initialState,
);

export default infoboxReducer;
